import React from 'react';
import cn from 'classnames';
import { parseISO } from 'date-fns';

import s from './ClientOrdersFields.module.css';

const fields = [];

const registerField = ({ label, render, ...opts }) => {
  const renderFn = typeof render === 'string' ? obj => obj[render] : render;
  const labelFn = typeof label === 'string' ? t => t(label) : label;
  fields.push({ label: labelFn, render: renderFn, ...opts });
};

const isEmpty = value => value === undefined || value === null || value === '';
const withEmpty = (value, fn) => (isEmpty(value) ? undefined : fn(value));

const findClientOrderField = name => fields.find(field => field.name === name);

const getClientOrdersFields = opts => {
  const { filter } = opts || {};

  return filter ? fields.filter(field => filter.includes(field.name)) : fields;
};

const ORDER_STATUSES = {
  CANCELED: 'Отменен',
  CONFIRMED: 'Подтвержден',
  PENDING: 'Выполняется',
  PLANNED: 'Запланирован',
};

registerField({
  label: 'Дата и время',
  name: 'datetime',
  type: 'date',
  render: ({ datetime }, t) => t('{{datetime, datetime_short}}', { datetime: parseISO(datetime) }),
});

registerField({
  name: 'status',
  type: 'select',
  items: [
    { value: 'CANCELED', label: t => t('Отменен') },
    { value: 'CONFIRMED', label: t => t('Подтвержден') },
    { value: 'PENDING', label: t => t('Выполняется') },
    { value: 'PLANNED', label: t => t('Запланирован') },
  ],
  readonly: true,
  render: (obj, t) => {
    const status = obj.status;
    return (
      <div className={cn(s.status, s[status.toLowerCase()])}>
        {withEmpty(status, () => ORDER_STATUSES[status] && t(ORDER_STATUSES[status]))}
      </div>
    );
  },
  label: 'Статус',
  parse: v => (isEmpty(v) ? 'NEW' : v),
});

registerField({
  label: 'Сумма',
  name: 'amount',
  type: 'number',
  render: ({ amount }, t) => <span className={s.amount}>{t('{{amount, currency}}', { amount })}</span>,
});

registerField({
  label: 'Наименование',
  name: 'title',
  type: 'text',
  render: ({ title }) => title,
});

registerField({
  label: 'Комментарий',
  name: 'comment',
  type: 'text',
  render: ({ comment }) => comment,
});

registerField({
  label: 'Имя',
  name: 'clients.firstName',
  type: 'text',
  render: ({ client }) => client?.firstName,
  system: true,
});

registerField({
  label: 'Фамилия',
  name: 'clients.lastName',
  type: 'text',
  render: ({ client }) => client?.lastName,
  system: true,
});

registerField({
  label: 'Дата Рождения',
  name: 'clients.bday',
  type: 'date',
  render: ({ client }, t) => withEmpty(client?.bday, () => t('{{date, date}}', { date: new Date(client?.bday) })),
  system: true,
});

registerField({
  label: 'Номер Карты',
  name: 'clients.cardNumber',
  type: 'text',
  render: ({ client }) => client?.cardNumber,
  system: true,
});

registerField({
  name: 'clients.email',
  type: 'email',
  render: ({ client }) => client?.email,
  label: 'Е-мейл',
  parse: v => withEmpty(v, v => v),
  system: true,
});

registerField({
  name: 'clients.insertedAt',
  type: 'date',
  render: ({ client }, t) =>
    withEmpty(client?.insertedAt, () => t('{{date, date}}', { date: new Date(client?.insertedAt) })),
  label: 'Дата Добавления',
  parse: v => withEmpty(v, v => v),
  system: true,
});

registerField({
  label: 'Телефон',
  name: 'clients.phone',
  type: 'text',
  render: ({ client }) => client?.phone,
  system: true,
});

registerField({
  label: 'Пол',
  name: 'clients.gender',
  type: 'text',
  render: ({ client }, t) => {
    if (client?.gender === 'MALE') return t('муж');
    if (client?.gender === 'FEMALE') return t('жен');
  },
  system: true,
});

registerField({
  label: 'Доп. Поле 1 Клиента',
  name: 'clients.extra1',
  type: 'text',
  render: ({ client }) => client?.extra1,
  system: true,
});

registerField({
  label: 'Доп. Поле 1',
  name: 'extra1',
  type: 'text',
  render: ({ extra1 }) => extra1,
});

registerField({
  label: 'Доп. Поле 2',
  name: 'extra2',
  type: 'text',
  render: ({ extra2 }) => extra2,
});

registerField({
  label: 'Доп. Поле 3',
  name: 'extra3',
  type: 'text',
  render: ({ extra3 }) => extra3,
});

registerField({
  label: 'Доп. Поле 4',
  name: 'extra4',
  type: 'text',
  render: ({ extra4 }) => extra4,
});

registerField({
  label: 'Доп. Поле 5',
  name: 'extra5',
  type: 'text',
  render: ({ extra5 }) => extra5,
});

registerField({
  label: 'Клиент',
  name: 'clients.id',
  type: 'present',
  render: ({ client }, t) => {
    if (client?.id) return t('Указано');
    return t('Не указано');
  },
  system: true,
});

export { getClientOrdersFields, findClientOrderField };
